import { useEffect, useMemo, useRef, useState } from 'react';
import useGameStore from '../store/gameStore';
import useCloudStore from '../store/cloudStore';
import type { AIArchetypeChoice, LobbyConfig, SetupPlayer, VictoryCondition } from '../types/gameTypes';
import { LOBBY_COLORS } from '../types/gameTypes';
import { money } from '../lib/format';
import { useT } from '../i18n';
import { STAKKO_EVENTS, StakkoAvatar, stakkoBus, useStakkoOptional } from '../stakko';
import {
  ArrowRightIcon,
  BotIcon,
  ChartIcon,
  CheckIcon,
  CloudIcon,
  CloseIcon,
  CoinsIcon,
  CrownIcon,
  DiceIcon,
  FlagIcon,
  FundIcon,
  PlayIcon,
  PlusIcon,
  ShieldIcon,
  SparkIcon,
  TrophyIcon,
  UserIcon,
  UsersIcon,
} from './GameIcons';

const BOT_NAMES = ['Venture Bot', 'Morgan', 'Prudence Bot', 'Vulture Bot', 'Bull Run', 'Bear Cave', 'Connector', 'Tycoon Prime', 'Rainmaker', 'Dealmaker'];

const usedColorsOf = (players: SetupPlayer[]) => new Set(players.map((p) => p.color));
const nextFreeColor = (players: SetupPlayer[]) => LOBBY_COLORS.find((c) => !usedColorsOf(players).has(c)) ?? LOBBY_COLORS[0];

interface LobbyScreenProps {
  /** Hide the landing page and reveal the live match (or the freshly started one). */
  onEnterGame?: () => void;
}

const ControlToggle = ({ slot, updateSlot }: { slot: SetupPlayer; updateSlot: (id: string, patch: Partial<SetupPlayer>) => void }) => {
  const t = useT();
  return (
    <div className="flex overflow-hidden rounded-lg border border-white/15 text-[11px] font-bold">
      <button
        type="button"
        onClick={() => updateSlot(slot.id, { isAI: false })}
        className={`flex items-center gap-1 px-2.5 py-1.5 transition ${!slot.isAI ? 'bg-cyan-400 text-slate-950' : 'bg-slate-800/80 text-slate-400 hover:bg-slate-700'}`}
      >
        <UserIcon size={12} /> {t('lobby.human')}
      </button>
      <button
        type="button"
        onClick={() => updateSlot(slot.id, { isAI: true })}
        className={`flex items-center gap-1 px-2.5 py-1.5 transition ${slot.isAI ? 'bg-violet-400 text-slate-950' : 'bg-slate-800/80 text-slate-400 hover:bg-slate-700'}`}
      >
        <BotIcon size={12} /> {t('lobby.bot')}
      </button>
    </div>
  );
};

const AccountChip = () => {
  const t = useT();
  const stakko = useStakkoOptional();
  const user = stakko?.user;
  const name = stakko?.profile?.displayName || user?.displayName || user?.email?.split('@')[0] || '';
  const plan = stakko?.entitlements.plan ?? 'free';

  if (!user) {
    return (
      <button
        type="button"
        onClick={() => stakkoBus.emit(STAKKO_EVENTS.openPanel, 'auth')}
        className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-xs font-bold text-slate-200 transition hover:border-amber-300/50 hover:bg-white/10"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-400/15 text-amber-200">
          <UserIcon size={15} />
        </span>
        {t('lobby.signIn')}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => stakkoBus.emit(STAKKO_EVENTS.openPanel, 'account')}
      className="flex items-center gap-2 rounded-xl border border-emerald-300/30 bg-emerald-400/10 px-3 py-2 text-xs font-bold text-emerald-100 transition hover:bg-emerald-400/20"
      title={t('lobby.account')}
    >
      <StakkoAvatar name={name} photoURL={stakko?.profile?.photoURL || user.photoURL} size={28} />
      <span className="max-w-[10rem] truncate">{name}</span>
      <span className="rounded-full bg-emerald-300/20 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-emerald-100">{plan}</span>
    </button>
  );
};

const Feature = ({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) => (
  <div className="flex items-start gap-2.5 rounded-2xl border border-white/10 bg-black/20 p-3">
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-400/15 text-amber-200">{icon}</span>
    <div className="min-w-0">
      <p className="text-xs font-black text-slate-100">{title}</p>
      <p className="mt-0.5 text-[11px] leading-snug text-slate-400">{body}</p>
    </div>
  </div>
);

const LobbyScreen = ({ onEnterGame }: LobbyScreenProps) => {
  const store = useGameStore();
  const t = useT();
  const { saveSlots, saveSlotsLoading, refreshSaveSlots, pending, loadNamedSlot, acceptResume, dismissPrompt, user: cloudUser, status } = useCloudStore();
  const [slots, setSlots] = useState<SetupPlayer[]>(() => store.lobby.players.map((p) => ({ ...p })));
  const [startingCash, setStartingCash] = useState<number>(store.lobby.startingCash);
  const [victory, setVictory] = useState<VictoryCondition>(store.lobby.victory);
  const setupRef = useRef<HTMLDivElement | null>(null);

  // The Stakko panel signs the player in; once the account lands, pull every save
  // document from `users/{uid}/games` (named slots, autosave and archived matches).
  useEffect(() => {
    if (cloudUser) void refreshSaveSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloudUser?.uid]);

  const updateSlot = (id: string, patch: Partial<SetupPlayer>) =>
    setSlots((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const addPlayer = () => {
    if (slots.length >= 10) return;
    const index = slots.length + 1;
    const usedNames = new Set(slots.map((s) => s.name));
    const botName = BOT_NAMES.find((n) => !usedNames.has(n)) ?? `Bot ${index}`;
    setSlots((prev) => [...prev, { id: `slot-${index}-${Date.now()}`, name: botName, isAI: true, archetype: 'RANDOM', color: nextFreeColor(prev) }]);
  };

  const removePlayer = (id: string) => {
    if (slots.length <= 2) return;
    setSlots((prev) => prev.filter((s) => s.id !== id));
  };

  const start = () => {
    // A new match must not be hidden behind the old cloud-save modal.
    dismissPrompt();
    const config: LobbyConfig = {
      players: slots.map((s, i) => ({ ...s, name: s.name.trim() || (s.isAI ? `Bot ${i + 1}` : `Player ${i + 1}`) })),
      startingCash,
      victory,
    };
    store.startGame(config);
    onEnterGame?.();
  };

  const localMatch = store.gameStarted;
  const localLeader = useMemo(() => {
    if (!localMatch) return null;
    return [...store.players].sort((a, b) => store.netWorthOf(b.id) - store.netWorthOf(a.id))[0] ?? null;
  }, [localMatch, store.players, store]);

  const cloudTarget = pending ?? saveSlots[0] ?? null;
  const cloudRound = cloudTarget ? ('summary' in cloudTarget ? cloudTarget.summary.round : cloudTarget.market.currentRound) : 0;
  const cloudPlayers = cloudTarget ? ('summary' in cloudTarget ? cloudTarget.summary.playerCount : cloudTarget.players.length) : 0;
  const cloudLeader = cloudTarget ? ('summary' in cloudTarget ? cloudTarget.summary.leaderName : [...cloudTarget.players].sort((a, b) => b.cash - a.cash)[0]?.name) : '';
  const cloudBusy = status === 'LOADING' || status === 'SAVING';

  const resumeCloud = () => {
    if (pending) acceptResume();
    else if (cloudTarget && !('summary' in cloudTarget)) acceptResume();
    else if (saveSlots[0]) void loadNamedSlot(saveSlots[0].id);
    onEnterGame?.();
  };

  const resumeSlot = async (saveId: string) => {
    await loadNamedSlot(saveId);
    onEnterGame?.();
  };

  const scrollToSetup = () => setupRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="lobby-bg min-h-dvh w-full overflow-y-auto text-slate-100">
      <div className="mx-auto flex min-h-full w-full max-w-6xl flex-col gap-6 px-4 py-5 sm:px-6 lg:px-8">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-amber-300/40 bg-gradient-to-br from-amber-300 to-orange-500 text-slate-950 shadow-[0_6px_24px_rgba(245,158,11,0.35)]">
              <DiceIcon size={24} />
            </span>
            <div>
              <h1 className="text-xl font-black tracking-tight text-white">Frakto</h1>
              <p className="text-[11px] font-semibold text-slate-400">{t('lobby.taglineShort')}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <AccountChip />
            <button
              type="button"
              onClick={() => scrollToSetup()}
              className="flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-amber-300 to-orange-500 px-3.5 py-2 text-xs font-black text-slate-950 shadow-lg transition hover:from-amber-200 hover:to-orange-400"
            >
              <PlayIcon size={14} /> {t('lobby.newGame')}
            </button>
          </div>
        </header>

        <section className="grid gap-5 lg:grid-cols-[1.35fr_1fr] lg:items-stretch">
          <div className="flex flex-col justify-center rounded-3xl border border-white/10 bg-black/20 p-5 sm:p-7">
            <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.24em] text-amber-300">
              <SparkIcon size={13} /> {t('lobby.eyebrow')}
            </p>
            <h2 className="mt-2 max-w-xl text-3xl font-black leading-tight text-white sm:text-4xl">
              {t('lobby.heroTitle')}
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-400">{t('lobby.heroBody')}</p>
            <div className="mt-4 grid gap-2 sm:grid-cols-3">
              <Feature icon={<ChartIcon size={17} />} title={t('lobby.featureEquity')} body={t('lobby.featureEquityBody')} />
              <Feature icon={<FundIcon size={17} />} title={t('lobby.featureFinance')} body={t('lobby.featureFinanceBody')} />
              <Feature icon={<BotIcon size={17} />} title={t('lobby.featureBots')} body={t('lobby.featureBotsBody')} />
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {localMatch && (
              <div className="rounded-3xl border border-emerald-300/40 bg-gradient-to-br from-emerald-500/20 to-teal-500/5 p-4 shadow-[0_18px_50px_rgba(16,185,129,0.15)]">
                <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-200">
                  <CrownIcon size={13} /> {t('lobby.localMatch')}
                </p>
                <p className="mt-1 text-lg font-black text-white">{t('lobby.continueTitle')}</p>
                <p className="mt-1 text-xs text-slate-300">
                  {t('lobby.continueBody', {
                    round: store.market.currentRound,
                    players: store.players.length,
                    leader: localLeader?.name ?? '—',
                  })}
                </p>
                <button
                  type="button"
                  onClick={() => onEnterGame?.()}
                  className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-2xl border-2 border-emerald-200/60 bg-gradient-to-r from-emerald-300 to-teal-300 px-4 py-2.5 text-sm font-black text-slate-950 shadow-lg transition hover:from-emerald-200 hover:to-teal-200"
                >
                  <PlayIcon size={17} /> {t('lobby.continueCta')} <ArrowRightIcon size={15} />
                </button>
              </div>
            )}

            {!localMatch && cloudTarget && (
              <div className="rounded-3xl border border-amber-300/40 bg-gradient-to-br from-amber-400/20 to-orange-500/5 p-4 shadow-[0_18px_50px_rgba(245,158,11,0.15)]">
                <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-amber-200">
                  <CloudIcon size={13} /> {t('lobby.cloudMatch')}
                </p>
                <p className="mt-1 text-lg font-black text-white">{t('lobby.continueTitle')}</p>
                <p className="mt-1 text-xs text-slate-300">
                  {t('lobby.continueBody', { round: cloudRound, players: cloudPlayers, leader: cloudLeader || '—' })}
                </p>
                <button
                  type="button"
                  disabled={cloudBusy}
                  onClick={resumeCloud}
                  className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-2xl border-2 border-amber-200/60 bg-gradient-to-r from-amber-300 to-orange-400 px-4 py-2.5 text-sm font-black text-slate-950 shadow-lg transition hover:from-amber-200 hover:to-orange-300 disabled:opacity-50"
                >
                  <CloudIcon size={17} /> {t('lobby.resumeCloudCta')} <ArrowRightIcon size={15} />
                </button>
              </div>
            )}

            {!localMatch && !cloudTarget && (
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4">
                <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                  <FlagIcon size={13} /> {t('lobby.noMatch')}
                </p>
                <p className="mt-1 text-sm font-bold text-slate-200">{t('lobby.noMatchBody')}</p>
                <button
                  type="button"
                  onClick={scrollToSetup}
                  className="mt-3 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-2xl border-2 border-white/15 bg-white/10 px-4 py-2.5 text-sm font-black text-slate-100 transition hover:bg-white/15"
                >
                  <PlusIcon size={16} /> {t('lobby.startSetup')}
                </button>
              </div>
            )}

            <div className="rounded-3xl border border-white/10 bg-black/20 p-4">
              <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                <ShieldIcon size={13} /> {t('lobby.account')}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-slate-400">
                {cloudUser ? t('lobby.signedInAs', { name: cloudUser.displayName || cloudUser.email }) : t('lobby.signInBody')}
              </p>
              {!cloudUser && (
                <button
                  type="button"
                  onClick={() => stakkoBus.emit(STAKKO_EVENTS.openPanel, 'auth')}
                  className="mt-2 flex min-h-[40px] w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-xs font-bold text-slate-200 transition hover:bg-white/10"
                >
                  <UserIcon size={14} /> {t('lobby.signIn')}
                </button>
              )}
              <p className="mt-2 flex items-center gap-1.5 text-[10px] text-slate-500">
                <CheckIcon size={11} /> {t('lobby.localNote')}
              </p>
            </div>

            {(cloudUser || saveSlots.length > 0) && (
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                    <CloudIcon size={13} /> {t('lobby.savesTitle')} ({saveSlots.length})
                  </p>
                  <button
                    type="button"
                    onClick={() => void refreshSaveSlots()}
                    disabled={saveSlotsLoading}
                    className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-[10px] font-bold text-slate-300 transition hover:bg-white/10 disabled:opacity-50"
                  >
                    {t('lobby.refresh')}
                  </button>
                </div>
                {saveSlotsLoading ? (
                  <p className="mt-2 text-xs text-slate-400">{t('lobby.savesLoading')}</p>
                ) : saveSlots.length === 0 ? (
                  <p className="mt-2 text-xs text-slate-400">{t('lobby.savesEmpty')}</p>
                ) : (
                  <div className="mt-2 max-h-64 space-y-1.5 overflow-y-auto pr-1 scrollbar-thin">
                    {saveSlots.map((slot) => {
                      const kindLabel =
                        slot.kind === 'ACTIVE'
                          ? t('lobby.saveKindActive')
                          : slot.kind === 'ARCHIVED'
                            ? t('lobby.saveKindArchived')
                            : t('lobby.saveKindNamed');
                      return (
                        <div key={slot.id} className="flex items-center gap-2 rounded-xl border border-white/10 bg-slate-950/50 p-2">
                          <div className="min-w-0 flex-1">
                            <p className="flex items-center gap-1.5 truncate text-[11px] font-bold text-slate-100">
                              <span className="rounded bg-white/10 px-1 py-0.5 text-[8px] uppercase tracking-wider text-slate-300">{kindLabel}</span>
                              <span className="truncate">{slot.name}</span>
                            </p>
                            <p className="mt-0.5 truncate text-[10px] text-slate-400">
                              {t('lobby.saveMeta', {
                                round: slot.summary.round,
                                players: slot.summary.playerCount,
                                leader: slot.summary.leaderName,
                              })}
                              {slot.updatedAt > 0 ? ` · ${new Date(slot.updatedAt).toLocaleDateString()}` : ''}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => void resumeSlot(slot.id)}
                            className="flex shrink-0 items-center gap-1 rounded-lg border border-emerald-300/40 bg-emerald-400/15 px-2.5 py-1.5 text-[10px] font-black text-emerald-100 transition hover:bg-emerald-400/25"
                          >
                            <PlayIcon size={11} /> {t('lobby.load')}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        <section ref={setupRef} className="scroll-mt-4 rounded-3xl border border-white/10 bg-black/25 p-4 sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.18em] text-amber-300">
                <UsersIcon size={15} /> {t('lobby.setupTitle')} ({slots.length}/10)
              </h2>
              <p className="mt-0.5 text-[11px] text-slate-400">{t('lobby.setupHint')}</p>
            </div>
            <button
              type="button"
              onClick={addPlayer}
              disabled={slots.length >= 10}
              className="flex items-center gap-1.5 rounded-xl bg-emerald-500/90 px-3 py-2 text-xs font-black text-slate-950 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-500"
            >
              <PlusIcon size={14} /> {t('lobby.addSeat')}
            </button>
          </div>

          <div className="mt-3 grid gap-2">
            {slots.map((slot, index) => (
              <div key={slot.id} className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/10 bg-slate-950/40 p-2.5">
                <button
                  type="button"
                  title={t('lobby.changeColor')}
                  onClick={() => {
                    const free = LOBBY_COLORS.filter((c) => !usedColorsOf(slots).has(c) || c === slot.color);
                    const next = free[(free.indexOf(slot.color) + 1) % free.length];
                    updateSlot(slot.id, { color: next });
                  }}
                  className="h-8 w-8 shrink-0 rounded-full border-2 border-white/40 shadow"
                  style={{ backgroundColor: slot.color }}
                />
                <span className="w-5 text-center text-[11px] font-bold text-slate-500">{index + 1}</span>
                <input
                  value={slot.name}
                  onChange={(e) => updateSlot(slot.id, { name: e.target.value })}
                  placeholder={`Player ${index + 1}`}
                  className="min-w-[120px] flex-1 rounded-lg border border-white/10 bg-black/30 px-3 py-1.5 text-sm font-semibold text-slate-100 outline-none focus:border-amber-300/60"
                />
                <ControlToggle slot={slot} updateSlot={updateSlot} />
                {slot.isAI && (
                  <select
                    value={slot.archetype}
                    onChange={(e) => updateSlot(slot.id, { archetype: e.target.value as AIArchetypeChoice })}
                    className="rounded-lg border border-white/10 bg-slate-900 px-2 py-1.5 text-[11px] font-bold text-slate-200"
                  >
                    <option value="AGGRESSIVE">{t('lobby.aggressive')}</option>
                    <option value="CONSERVATIVE">{t('lobby.conservative')}</option>
                    <option value="OPPORTUNISTIC">{t('lobby.opportunistic')}</option>
                    <option value="RANDOM">{t('lobby.random')}</option>
                  </select>
                )}
                <button
                  type="button"
                  onClick={() => removePlayer(slot.id)}
                  disabled={slots.length <= 2}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-rose-600/70 text-white transition hover:bg-rose-500 disabled:cursor-not-allowed disabled:bg-slate-800 disabled:text-slate-600"
                  title={t('lobby.removeSeat')}
                >
                  <CloseIcon size={14} />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-3">
              <h3 className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                <CoinsIcon size={13} /> {t('lobby.startingCapital')}
              </h3>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {[1500, 2500, 5000].map((cash) => (
                  <button
                    key={cash}
                    type="button"
                    onClick={() => setStartingCash(cash)}
                    className={`min-h-[44px] rounded-xl border-2 px-2 py-2 text-xs font-black transition ${
                      startingCash === cash ? 'border-emerald-300 bg-emerald-400/20 text-emerald-100' : 'border-white/10 bg-black/20 text-slate-300 hover:border-white/25'
                    }`}
                  >
                    {money(cash)}
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-slate-950/40 p-3">
              <h3 className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                <TrophyIcon size={13} /> {t('lobby.victory')}
              </h3>
              <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
                {(
                  [
                    { value: 'NET_WORTH_100K' as VictoryCondition, label: t('lobby.victory100k') },
                    { value: 'NET_WORTH_150K' as VictoryCondition, label: t('lobby.victory150k') },
                    { value: 'LAST_STANDING' as VictoryCondition, label: t('lobby.victoryLast') },
                    { value: 'ENDLESS' as VictoryCondition, label: t('lobby.victoryEndless') },
                  ]
                ).map((entry) => (
                  <button
                    key={entry.value}
                    type="button"
                    onClick={() => setVictory(entry.value)}
                    className={`rounded-xl border-2 px-2.5 py-2 text-left text-[11px] font-bold transition ${
                      victory === entry.value ? 'border-amber-300 bg-amber-400/15 text-amber-100' : 'border-white/10 bg-black/20 text-slate-300 hover:border-white/25'
                    }`}
                  >
                    {entry.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={start}
            disabled={slots.length < 2}
            className="mt-4 flex min-h-[56px] w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-amber-300 via-orange-400 to-rose-400 px-6 py-3 text-base font-black text-slate-950 shadow-[0_10px_36px_rgba(245,158,11,0.35)] transition hover:from-amber-200 hover:via-orange-300 hover:to-rose-300 disabled:cursor-not-allowed disabled:from-slate-800 disabled:via-slate-800 disabled:to-slate-800 disabled:text-slate-500"
          >
            <PlayIcon size={18} /> {t('lobby.startGame')}
          </button>
          <p className="mt-2 text-center text-[10px] text-slate-500">{t('lobby.startHint')}</p>
        </section>
      </div>
    </div>
  );
};

export default LobbyScreen;
